import React from "react";
import {
  FiBox,
  FiCalendar,
  FiChevronRight,
  FiClock,
  FiCreditCard,
} from "react-icons/fi";
import { FaMoneyBillWave } from "react-icons/fa";
import { RiWallet3Line } from "react-icons/ri";

const PAYMENT_META = {
  cod: {
    label: "Cash on Delivery",
    icon: FaMoneyBillWave,
    accent: "text-[#0b8f36]",
    bg: "bg-[#eef9f0]",
  },
  online: {
    label: "Online",
    icon: FiCreditCard,
    accent: "text-[#5b6478]",
    bg: "bg-[#f4f6fa]",
  },
  wallet: {
    label: "Wallet",
    icon: RiWallet3Line,
    accent: "text-[#5b6478]",
    bg: "bg-[#f4f6fa]",
  },
};

const STATUS_META = {
  delivered: {
    label: "Delivered",
    className: "bg-[#0a8f2f] text-white",
  },
  processing: {
    label: "Out for Delivery",
    className: "bg-[#f5a300] text-white",
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-[#ef4444] text-white",
  },
  new: {
    label: "Placed",
    className: "bg-[#6b7280] text-white",
  },
};

const formatAmount = (amount) =>
  `₹${Math.round(Number(amount) || 0)}`;

const formatDate = (value) => {
  const date = new Date(value || Date.now());

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value) => {
  const date = new Date(value || Date.now());

  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const normalizePayment = (value) =>
  String(value || "").toLowerCase();

const normalizeStatus = (value) =>
  String(value || "").toLowerCase();

const OrderCard = ({ order, onClick }) => {
  const paymentKey = normalizePayment(order?.payment_method);

  const payment =
    PAYMENT_META[paymentKey] || PAYMENT_META.online;

  const status =
    STATUS_META[normalizeStatus(order?.status)] || {
      label: order?.status || "Unknown",
      className: "bg-[#6b7280] text-white",
    };

  const PaymentIcon = payment.icon;

  return (
    <div
      className="
        w-full
        min-w-0
        max-w-full
        overflow-hidden
        rounded-[18px]
        border
        border-[#ece4d6]
        bg-white
        px-2.5
        py-3
        shadow-[0_8px_24px_rgba(179,151,102,0.08)]
        transition
        hover:shadow-[0_14px_30px_rgba(179,151,102,0.12)]
        sm:rounded-[20px]
        sm:px-4
        sm:py-4
      "
    >
      <div
        className="
          grid
          w-full
          min-w-0
          max-w-full
          gap-3
          sm:gap-4
          lg:grid-cols-[70px_minmax(0,1.05fr)_minmax(0,.95fr)_minmax(0,.95fr)_146px]
          lg:items-center
        "
      >
        {/* MOBILE TOP SECTION */}
        <div
          className="
            flex
            w-full
            min-w-0
            max-w-full
            items-start
            gap-2.5
            sm:gap-4
            lg:contents
          "
        >
          {/* ORDER ICON */}
          <div
            className="
              flex
              h-[48px]
              w-[48px]
              shrink-0
              items-center
              justify-center
              rounded-[14px]
              bg-[linear-gradient(180deg,#fff8ea_0%,#fef4de_100%)]
              sm:h-[64px]
              sm:w-[64px]
              sm:rounded-[18px]
              lg:h-[70px]
              lg:w-[70px]
            "
          >
            <FiBox
              className="
                text-[24px]
                text-[#108234]
                sm:text-[30px]
                lg:text-[32px]
              "
            />
          </div>

          {/* MOBILE ORDER INFO */}
          <div className="min-w-0 flex-1 lg:hidden">
            <div
              className="
                grid
                min-w-0
                grid-cols-[minmax(0,1fr)_auto]
                items-start
                gap-2
              "
            >
              {/* AMOUNT + ORDER ID */}
              <div className="min-w-0">
                <p
                  className="
                    m-0
                    text-[20px]
                    font-extrabold
                    leading-none
                    text-[#0b8f36]
                    sm:text-[24px]
                  "
                >
                  {formatAmount(order?.amount)}
                </p>

                <p
                  className="
                    mt-1.5
                    min-w-0
                    break-words
                    text-[12px]
                    font-semibold
                    leading-[1.35]
                    text-[#1f2937]
                    [overflow-wrap:anywhere]
                    sm:text-[14px]
                  "
                >
                  Order #{order?.orderId || "N/A"}
                </p>
              </div>

              {/* MOBILE STATUS */}
              <span
                className={`
                  inline-flex
                  min-h-[32px]
                  min-w-[88px]
                  max-w-[108px]
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  px-2
                  py-1.5
                  text-center
                  text-[9px]
                  font-semibold
                  uppercase
                  leading-[1.25]
                  tracking-[0.01em]
                  whitespace-normal
                  sm:min-w-[108px]
                  sm:max-w-[130px]
                  sm:px-3
                  sm:text-[10px]
                  ${status.className}
                `}
              >
                {normalizeStatus(order?.status) ===
                  "processing" && (
                  <FiClock className="mr-1 shrink-0 text-[10px] sm:text-[11px]" />
                )}

                <span className="min-w-0">
                  {status.label}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* DESKTOP ORDER INFO */}
        <div className="hidden min-w-0 lg:block">
          <p className="text-[26px] font-extrabold leading-none text-[#0b8f36]">
            {formatAmount(order?.amount)}
          </p>

          <p className="mt-2 break-words text-[14px] font-semibold text-[#1f2937] [overflow-wrap:anywhere]">
            Order #{order?.orderId || "N/A"}
          </p>
        </div>

        {/* DATE & TIME */}
        <div
          className="
            w-full
            min-w-0
            max-w-full
            rounded-[16px]
            bg-[#fffdfa]
            p-3
            lg:rounded-none
            lg:border-l
            lg:border-[#ece4d6]
            lg:bg-transparent
            lg:p-0
            lg:pl-4
          "
        >
          <div className="flex min-w-0 items-start gap-2.5">
            <div
              className="
                mt-0.5
                flex
                h-8
                w-8
                shrink-0
                items-center
                justify-center
                rounded-[10px]
                bg-[#f7f9fc]
                text-[#5b6478]
              "
            >
              <FiCalendar className="text-[15px]" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-medium text-[#8a94a6]">
                Date & Time
              </p>

              <p
                className="
                  mt-1
                  min-w-0
                  break-words
                  text-[13px]
                  font-medium
                  leading-5
                  text-[#4a5568]
                  sm:text-[14px]
                "
              >
                {formatDate(order?.time_stamp)},{" "}
                {formatTime(order?.time_stamp)}
              </p>
            </div>
          </div>
        </div>

        {/* PAYMENT METHOD */}
        <div
          className="
            w-full
            min-w-0
            max-w-full
            rounded-[16px]
            bg-[#fffdfa]
            p-3
            lg:rounded-none
            lg:border-l
            lg:border-[#ece4d6]
            lg:bg-transparent
            lg:p-0
            lg:pl-4
          "
        >
          <div className="flex min-w-0 items-start gap-2.5">
            <div
              className={`
                mt-0.5
                flex
                h-8
                w-8
                shrink-0
                items-center
                justify-center
                rounded-[10px]
                ${payment.bg}
                ${payment.accent}
              `}
            >
              <PaymentIcon className="text-[15px]" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-medium text-[#8a94a6]">
                Payment Method
              </p>

              <p
                className="
                  mt-1
                  min-w-0
                  break-words
                  text-[13px]
                  font-medium
                  text-[#4a5568]
                  sm:text-[14px]
                "
              >
                {payment.label}
              </p>
            </div>
          </div>
        </div>

        {/* STATUS + VIEW DETAILS */}
        <div
          className="
            flex
            w-full
            min-w-0
            max-w-full
            flex-col
            gap-2.5
            lg:items-end
            lg:gap-3
          "
        >
          {/* DESKTOP STATUS */}
          <span
            className={`
              hidden
              min-w-[128px]
              max-w-full
              items-center
              justify-center
              rounded-full
              px-4
              py-2
              text-center
              text-[12px]
              font-semibold
              uppercase
              tracking-[0.02em]
              lg:inline-flex
              ${status.className}
            `}
          >
            {normalizeStatus(order?.status) ===
              "processing" && (
              <FiClock className="mr-1.5 shrink-0 text-[12px]" />
            )}

            {status.label}
          </span>

          {/* VIEW DETAILS */}
          <button
            type="button"
            onClick={() => onClick(order)}
            className="
              inline-flex
              h-[42px]
              w-full
              min-w-0
              max-w-full
              items-center
              justify-center
              gap-2
              rounded-[12px]
              border
              border-[#49b35f]
              bg-white
              px-3
              text-[13px]
              font-semibold
              text-[#20863a]
              transition
              hover:bg-[#f6fff7]
              sm:h-[44px]
              sm:px-4
              sm:text-[14px]
              lg:w-auto
              lg:min-w-[132px]
            "
          >
            <span>View Details</span>

            <FiChevronRight className="shrink-0 text-[15px]" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;